import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import DoospLayout from "@/components/doosp/DoospLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, CheckCircle2, MessageSquare } from "lucide-react";
import { DOOSP_INFO, PROGRAMS } from "@/lib/programs";

const REASONS = [
  "General inquiry",
  "Program waitlist",
  "Corporate / bulk purchase",
  "Instructor application",
  "Media / press",
  "Verification support",
  "Other",
];

export default function Contact() {
  const [params] = useSearchParams();
  const preselectedProgram = params.get("program");
  const [form, setForm] = useState({
    name: "", email: "", organization: "", reason: preselectedProgram ? "Program waitlist" : "General inquiry",
    program: preselectedProgram || "", message: "",
  });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (preselectedProgram) {
      setForm((f) => ({ ...f, reason: "Program waitlist", program: preselectedProgram }));
    }
  }, [preselectedProgram]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      return toast.error("Name, email, and message are required");
    }
    // Purely client-side; server-side email isn't wired up (email features intentionally removed)
    setSent(true);
    toast.success("Message received — we&rsquo;ll get back to you shortly");
  };

  if (sent) {
    return (
      <DoospLayout>
        <section className="min-h-[60vh] flex items-center justify-center">
          <div className="max-w-lg mx-auto px-6 py-24 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-display text-3xl md:text-4xl font-bold" data-testid="contact-sent">Message received.</h1>
            <p className="text-zinc-600 mt-4">
              Thanks for reaching out, {form.name.split(" ")[0]}. A member of the DOOSP team will respond within 1–2 business days at{" "}
              <strong>{form.email}</strong>.
            </p>
            <p className="text-sm text-zinc-500 mt-8">
              For urgent matters email <a href={`mailto:${DOOSP_INFO.contactEmail}`} className="text-red-700 underline">{DOOSP_INFO.contactEmail}</a>.
            </p>
          </div>
        </section>
      </DoospLayout>
    );
  }

  return (
    <DoospLayout>
      <section className="border-b border-zinc-200 bg-gradient-to-b from-white to-zinc-50">
        <div className="max-w-5xl mx-auto px-6 py-20">
          <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-4">Contact</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="contact-heading">
            Talk to the DOOSP team.
          </h1>
          <p className="text-lg text-zinc-600 mt-6 max-w-2xl leading-relaxed">
            Program questions, waitlist sign-ups, corporate pricing, instructor applications, or
            certificate support — we&rsquo;re here.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <form onSubmit={submit} className="bg-white border border-zinc-200 rounded-xl p-8 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Full name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-name" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email" />
                </div>
                <div>
                  <Label>Organization</Label>
                  <Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="contact-org" />
                </div>
                <div>
                  <Label>Reason</Label>
                  <select
                    value={form.reason}
                    onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    className="w-full h-10 border border-zinc-200 rounded-md px-3 text-sm bg-white"
                    data-testid="contact-reason"
                  >
                    {REASONS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              {form.reason === "Program waitlist" && (
                <div>
                  <Label>Which program?</Label>
                  <select
                    value={form.program}
                    onChange={(e) => setForm({ ...form, program: e.target.value })}
                    className="w-full h-10 border border-zinc-200 rounded-md px-3 text-sm bg-white"
                    data-testid="contact-program"
                  >
                    <option value="">Select a program</option>
                    {PROGRAMS.filter((p) => p.status === "coming-soon").map((p) => (
                      <option key={p.slug} value={p.slug}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <Label>Message *</Label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={6}
                  required
                  className="w-full border border-zinc-200 rounded-md p-3 text-sm resize-none"
                  placeholder="Tell us how we can help…"
                  data-testid="contact-message"
                />
              </div>
              <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white h-11 px-6" data-testid="contact-submit">
                <Send className="mr-2 h-4 w-4" /> Send message
              </Button>
            </form>
          </div>

          <aside className="lg:col-span-4 space-y-5">
            <div className="bg-zinc-900 text-white rounded-xl p-6">
              <div className="text-xs font-mono uppercase tracking-widest text-red-400 mb-3">Direct contact</div>
              <div className="space-y-3">
                <a href={`mailto:${DOOSP_INFO.contactEmail}`} className="flex items-center gap-3 hover:text-red-300 transition">
                  <Mail className="h-4 w-4" />
                  <span className="text-sm">{DOOSP_INFO.contactEmail}</span>
                </a>
                <div className="flex items-center gap-3 text-zinc-400">
                  <MapPin className="h-4 w-4" />
                  <span className="text-sm">Global · remote-first</span>
                </div>
              </div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl p-6">
              <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-3">Response time</div>
              <p className="text-sm text-zinc-600">Most messages receive a reply within <strong>1–2 business days</strong>. Corporate and instructor inquiries are prioritized.</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <MessageSquare className="h-5 w-5 text-red-800 mb-3" />
              <div className="font-display font-bold">Certificate holders</div>
              <p className="text-sm text-zinc-700 mt-1">Already have a certificate? Log in to your dashboard to manage or re-download it.</p>
            </div>
          </aside>
        </div>
      </section>
    </DoospLayout>
  );
}
